import { AuthGradient } from '@/features/account/AuthGradient';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Card } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { APIError } from '@/lib/api/APIError';
import { getFirstAPIErrorFieldCopy } from '@/lib/api/apiErrorCopy';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import {
  authenticateWithApple,
  authenticateWithGoogle,
  authStore,
  toSession,
  useAuth,
} from '@/lib/auth/auth';
import { APP_CONFIG } from '@/lib/app/config';
import {
  clearSignupDraft,
  writeProfileSetupDraft,
  readSignupDraft,
  useOnboarding,
  writeSignupDraft,
} from '@/features/onboarding/onboarding';
import {
  ACCOUNT_VALIDATION_LIMITS,
  USERNAME_HELP_COPY,
  normalizeUsernameInput,
  validatePasswordConfirmation,
  validateEmail,
  validateProfileIdentity,
  validateUsername,
} from '@/features/account/accountValidation';
import { resolvePostSignInHref } from '@/features/account/authRedirect';
import {
  identifyPostHog,
  setAnalyticsMirrorAccessToken,
  trackAccountCreated,
  trackLogin,
  trackSignUpStarted,
  trackSignUpStepCompleted,
} from '@/lib/analytics/posthog';
import { addSentryActionBreadcrumb, createSentryInputTracker } from '@/lib/analytics/sentry';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import { cn } from '@/lib/utils';
import { signInWithGoogle, isGoogleSignInAvailable } from '@/lib/auth/googleAuth';
import { signInWithApple } from '@/lib/auth/appleAuth';
import * as AppleAuthentication from 'expo-apple-authentication';
import { Link, Redirect, router, useLocalSearchParams } from 'expo-router';
import {
  ArrowLeftIcon,
  CircleHelpIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  MailIcon,
  User2Icon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  Easing,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';

type Mode = 'login' | 'signup';
type SignupStep = 'credentials' | 'profile';
type AuthErrorContext =
  | 'apple'
  | 'google'
  | 'login'
  | 'reset'
  | 'signup'
  | 'support';

function firstReasonableName(value?: string | null) {
  const cleaned = (value ?? '').trim().replace(/\s+/g, ' ');
  if (
    !cleaned ||
    cleaned.includes('@') ||
    cleaned.length > ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength
  ) {
    return '';
  }
  return cleaned.split(' ')[0] ?? '';
}

function getAuthErrorMessage(error: unknown, context: AuthErrorContext) {
  if (context === 'support') {
    return 'We could not open support right now. Please try again in a moment.';
  }

  if (context === 'apple') {
    return 'Apple sign-in did not go through. Please try again.';
  }

  if (context === 'google') {
    return 'Google sign-in did not go through. Please try again.';
  }

  if (error instanceof APIError) {
    if (context === 'login') {
      if (error.status === 401) {
        return "That sign-in didn't work. If you don't have an account yet, create one.";
      }

      const fieldCopy = getFirstAPIErrorFieldCopy(error, ['email', 'password']);
      if (fieldCopy) {
        return fieldCopy;
      }

      if (error.status === 429) {
        return 'Too many sign-in attempts right now. Give it a minute, then try again.';
      }
    }

    if (context === 'signup') {
      const fieldCopy = getFirstAPIErrorFieldCopy(error, [
        'email',
        'password',
        'username',
        'display_name',
      ]);
      if (fieldCopy) {
        return fieldCopy;
      }

      if (error.status === 409) {
        return 'That email or username is already in use. Try a different one and keep going.';
      }

      if (error.status === 429) {
        return 'Too many account attempts right now. Give it a minute, then try again.';
      }
    }

    if (context === 'reset') {
      const fieldCopy = getFirstAPIErrorFieldCopy(error, ['email']);
      if (fieldCopy) {
        return fieldCopy;
      }

      if (error.status === 429) {
        return 'We have sent a few reset emails already. Wait a minute, then try again.';
      }
    }

    if (error.status >= 500) {
      return 'Something went wrong on our side. Please try again in a moment.';
    }
  }

  const message = error instanceof Error ? error.message : '';
  if (message.includes('Unable to reach API')) {
    return 'We could not reach Radious right now. Check your connection and try again.';
  }

  if (context === 'reset') {
    return 'We could not send that reset link right now. Please try again.';
  }

  if (context === 'signup') {
    return 'We could not create your account right now. Please try again.';
  }

  return 'We could not complete that sign-in right now. Please try again.';
}

export default function SignInScreen() {
  const insets = useSafeAreaInsets();
  const isDesktop = useIsDesktop();
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const isBusy = useAuth((state) => state.isBusy);
  const workspaceMode = useWorkspaceMode();
  const { currentContext, isHydrating: isAccountContextHydrating } = useAccountContext();
  const login = useAuth((state) => state.login);
  const requestPasswordReset = useAuth((state) => state.requestPasswordReset);
  const signup = useAuth((state) => state.signup);
  const markProfileSetupPending = useOnboarding((state) => state.markProfileSetupPending);
  const params = useLocalSearchParams<{ mode?: string; next?: string }>();
  const [mode, setMode] = React.useState<Mode>(params.mode === 'signup' ? 'signup' : 'login');
  const canGoBack = router.canGoBack();
  const postSignInHref = resolvePostSignInHref(params.next);
  const [signupStep, setSignupStep] = React.useState<SignupStep>('credentials');
  const [tabWidth, setTabWidth] = React.useState(0);
  const tabAnimation = React.useRef(new Animated.Value(mode === 'signup' ? 1 : 0)).current;
  const socialSignInPendingRef = React.useRef(false);
  const [isSocialSignInBusy, setIsSocialSignInBusy] = React.useState(false);

  const [appleAvailable, setAppleAvailable] = React.useState(false);

  React.useEffect(() => {
    setMode(params.mode === 'signup' ? 'signup' : 'login');
  }, [params.mode]);

  React.useEffect(() => {
    AppleAuthentication.isAvailableAsync()
      .then(setAppleAvailable)
      .catch(() => undefined);
  }, []);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [isSignupDraftHydrating, setIsSignupDraftHydrating] = React.useState(true);
  const emailInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'sign_in', field: 'email', form: 'auth' }),
    []
  );
  const passwordInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'sign_in', field: 'password', form: 'auth' }),
    []
  );
  const confirmPasswordInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'sign_in', field: 'confirm_password', form: 'auth' }),
    []
  );
  const usernameInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'sign_in', field: 'username', form: 'auth' }),
    []
  );
  const displayNameInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'sign_in', field: 'display_name', form: 'auth' }),
    []
  );

  React.useEffect(() => {
    setSignupStep('credentials');
    setError(null);
    setNotice(null);
    if (mode === 'signup') {
      trackSignUpStarted('email');
    }
  }, [mode]);

  React.useEffect(() => {
    let cancelled = false;

    if (mode !== 'signup') {
      setIsSignupDraftHydrating(false);
      return;
    }

    readSignupDraft()
      .then((draft) => {
        if (cancelled || !draft) {
          return;
        }

        setEmail((current) => current || draft.email);
        setUsername((current) => current || draft.username);
        setDisplayName((current) => current || draft.displayName);

        if (draft.step === 'profile') {
          setSignupStep('credentials');
          setNotice(
            'We saved your sign-up progress. Re-enter your password, then finish creating your account.'
          );
          return;
        }

        if (draft.email || draft.username || draft.displayName) {
          setNotice('We saved your sign-up draft. Finish your account details to keep going.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSignupDraftHydrating(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mode]);

  React.useEffect(() => {
    if (mode !== 'signup' || isSignupDraftHydrating) {
      return;
    }

    if (!email.trim() && !username.trim() && !displayName.trim()) {
      clearSignupDraft().catch(() => undefined);
      return;
    }

    writeSignupDraft({
      email: email.trim(),
      username: username.trim(),
      displayName: displayName.trim(),
      step: signupStep,
    }).catch(() => undefined);
  }, [displayName, email, isSignupDraftHydrating, mode, signupStep, username]);

  React.useEffect(() => {
    Animated.timing(tabAnimation, {
      toValue: mode === 'signup' ? 1 : 0,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [mode, tabAnimation]);

  React.useEffect(() => {
    const message = error ?? notice;
    if (!message) {
      return;
    }

    AccessibilityInfo.announceForAccessibility(message);
  }, [error, notice]);

  if (isHydrating || isAccountContextHydrating) {
    return <View style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }} />;
  }

  if (session) {
    if (
      !params.next &&
      workspaceMode.orgAccessEnabled &&
      currentContext?.kind === 'business' &&
      currentContext.activeOrgId
    ) {
      return <Redirect href={'/(business)/home' as any} />;
    }

    return <Redirect href={postSignInHref} />;
  }

  async function handleAppleSignIn() {
    if (socialSignInPendingRef.current) {
      return;
    }
    socialSignInPendingRef.current = true;
    setIsSocialSignInBusy(true);
    addSentryActionBreadcrumb({
      screen: 'sign_in',
      action: 'attempt_apple_sign_in',
      data: { mode },
    });
    try {
      setError(null);
      const appleResult = await signInWithApple();

      if (!appleResult.success) {
        if (appleResult.cancelled) return;
        setError(getAuthErrorMessage(appleResult.error, 'apple'));
        return;
      }

      const result = await authenticateWithApple({
        identityToken: appleResult.identityToken,
        fullName: appleResult.fullName,
        email: appleResult.email,
      });

      authStore.setState({ session: toSession(result) });
      clearSignupDraft().catch(() => undefined);
      setAnalyticsMirrorAccessToken(result.access_token);
      identifyPostHog({
        userID: result.user.id,
        email: result.user.email,
        username: result.profile.username,
      });
      if (result.is_new_user) {
        trackAccountCreated('apple');
        await markProfileSetupPending(result.user.id);
        await writeProfileSetupDraft({
          username: '',
          displayName: firstReasonableName(result.profile.display_name),
          bio: '',
          avatarUrl: '',
          interestTags: [],
        });
      } else {
        trackLogin('apple');
      }

      if (result.is_new_user) {
        router.replace({
          pathname: '/onboarding/profile-setup',
          params: { next: postSignInHref },
        });
      } else {
        router.replace(postSignInHref);
      }
    } catch (err: any) {
      if (__DEV__) console.error('Apple sign-in error:', err);
      setError(getAuthErrorMessage(err, 'apple'));
    } finally {
      socialSignInPendingRef.current = false;
      setIsSocialSignInBusy(false);
    }
  }

  async function handleGoogleSignIn() {
    if (!isGoogleSignInAvailable()) {
      setError('Google sign-in is not available on this device right now.');
      return;
    }
    if (socialSignInPendingRef.current) {
      return;
    }
    socialSignInPendingRef.current = true;
    setIsSocialSignInBusy(true);

    addSentryActionBreadcrumb({
      screen: 'sign_in',
      action: 'attempt_google_sign_in',
      data: { mode },
    });
    try {
      setError(null);
      const googleResult = await signInWithGoogle();

      if (!googleResult.success) {
        if (googleResult.cancelled) return; // User cancelled, no error
        setError(getAuthErrorMessage(googleResult.error, 'google'));
        return;
      }

      const result = await authenticateWithGoogle(googleResult.idToken);

      authStore.setState({ session: toSession(result) });
      clearSignupDraft().catch(() => undefined);
      setAnalyticsMirrorAccessToken(result.access_token);
      identifyPostHog({
        userID: result.user.id,
        email: result.user.email,
        username: result.profile.username,
      });
      if (result.is_new_user) {
        trackAccountCreated('google');
        await markProfileSetupPending(result.user.id);
        await writeProfileSetupDraft({
          username: '',
          displayName: firstReasonableName(result.profile.display_name),
          bio: '',
          avatarUrl: result.profile.avatar_url ?? '',
          interestTags: [],
        });
      } else {
        trackLogin('google');
      }

      if (result.is_new_user) {
        router.replace({
          pathname: '/onboarding/profile-setup',
          params: { next: postSignInHref },
        });
      } else {
        router.replace(postSignInHref);
      }
    } catch (err: any) {
      if (__DEV__) console.error('Google sign-in error:', err);
      setError(getAuthErrorMessage(err, 'google'));
    } finally {
      socialSignInPendingRef.current = false;
      setIsSocialSignInBusy(false);
    }
  }

  const isSignupProfile = mode === 'signup' && signupStep === 'profile';
  const usernameValidationError =
    isSignupProfile && username.trim() ? validateUsername(username) : null;
  const primaryLabel =
    mode === 'login' ? 'Sign in' : isSignupProfile ? 'Create account' : 'Continue';
  const helperCopy =
    mode === 'login'
      ? 'Pick up your plans, follows, and RSVPs right where you left them.'
      : isSignupProfile
        ? 'Choose the name people will recognize when they come across you on Radious.'
        : 'Create your account to save plans faster and keep your nights in one place.';

  const isPrimaryDisabled =
    isBusy ||
    isSocialSignInBusy ||
    (mode === 'login'
      ? !email.trim() || !password.trim()
      : signupStep === 'credentials'
        ? !email.trim() || !password.trim() || !confirmPassword.trim()
        : !username.trim() || !displayName.trim());

  const indicatorTranslateX =
    tabWidth > 0
      ? tabAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [4, tabWidth / 2],
        })
      : 0;

  async function handlePrimaryAction() {
    setError(null);
    addSentryActionBreadcrumb({
      screen: 'sign_in',
      action:
        mode === 'login'
          ? 'submit_login'
          : signupStep === 'credentials'
            ? 'continue_signup_credentials'
            : 'submit_signup_profile',
      data: {
        mode,
        signup_step: signupStep,
        has_email: Boolean(email.trim()),
        password_length: password.length,
        username_length: username.trim().length,
        display_name_length: displayName.trim().length,
      },
    });

    try {
      if (mode === 'login') {
        if (!email.trim() || !password.trim()) {
          setError('Enter your email and password.');
          return;
        }

        await login(email.trim(), password);
        await clearSignupDraft();
        // Navigation handled by <Redirect> in the render path once session is set.
        return;
      }

      if (signupStep === 'credentials') {
        if (!email.trim() || !password.trim()) {
          setError('Email and password are required.');
          return;
        }

        const emailError = validateEmail(email);
        if (emailError) {
          setError(emailError);
          return;
        }

        const passwordError = validatePasswordConfirmation(password, confirmPassword);
        if (passwordError) {
          setError(passwordError);
          return;
        }

        trackSignUpStepCompleted('credentials');
        setSignupStep('profile');
        return;
      }

      const profileError = validateProfileIdentity({ displayName, username });
      if (profileError) {
        setError(profileError);
        return;
      }

      await signup({
        email: email.trim(),
        password,
        username: username.trim(),
        displayName: displayName.trim(),
      });
      const userID = authStore.getState().session?.me.user.id;
      if (userID) {
        await markProfileSetupPending(userID);
        await writeProfileSetupDraft({
          username: username.trim(),
          displayName: displayName.trim(),
          bio: '',
          avatarUrl: '',
          interestTags: [],
        });
      }
      await clearSignupDraft();
      router.replace({
        pathname: '/onboarding/profile-setup',
        params: { next: postSignInHref },
      });
    } catch (submitError) {
      setError(getAuthErrorMessage(submitError, mode === 'login' ? 'login' : 'signup'));
    }
  }

  async function handlePasswordReset() {
    setError(null);
    setNotice(null);
    addSentryActionBreadcrumb({
      screen: 'sign_in',
      action: 'request_password_reset',
      data: {
        has_email: Boolean(email.trim()),
      },
    });

    if (!email.trim()) {
      setError('Add your email first, then we will send you a reset link.');
      return;
    }

    try {
      await requestPasswordReset(email.trim());
      setNotice('If that account exists, we sent a password reset link.');
    } catch (resetError) {
      setError(getAuthErrorMessage(resetError, 'reset'));
    }
  }

  function switchMode(nextMode: Mode) {
    if (nextMode === mode) {
      return;
    }

    setError(null);
    setNotice(null);
    setSignupStep('credentials');
    if (nextMode === 'signup') {
      setIsSignupDraftHydrating(true);
    }
    if (nextMode === 'login') {
      clearSignupDraft().catch(() => undefined);
    }
    setMode(nextMode);
  }

  return (
    <View
      className="flex-1"
      style={{ backgroundColor: DESIGN_TOKENS.colors.canvas, position: 'relative' }}>
      <AuthGradient />
      <ScrollView
        className="flex-1"
        style={{ zIndex: 1 }}
        automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: isDesktop ? 48 : Math.max(insets.bottom, 20),
        }}>
        <View
          className="flex-1 px-5 pb-10"
          style={{
            paddingTop: Math.max(insets.top, 16) + 10,
            width: '100%',
            maxWidth: isDesktop ? 520 : undefined,
            alignSelf: 'center',
          }}>
            <View className="flex-row items-center justify-between">
              {canGoBack ? (
                <Pressable
                  accessibilityRole="button"
                  className="size-11 items-center justify-center rounded-full border border-border bg-card"
                  onPress={() => {
                    if (router.canGoBack()) {
                      router.back();
                      return;
                    }
                    router.replace('/(tabs)/events');
                  }}>
                  <Icon as={ArrowLeftIcon} size={18} className="text-foreground" />
                </Pressable>
              ) : (
                <View className="size-11" />
              )}
              <Pressable
                accessibilityLabel="Open support"
                accessibilityRole="button"
                className="min-h-[44px] min-w-[44px] items-center justify-center rounded-full px-3"
                onPress={() =>
                  Linking.openURL(APP_CONFIG.supportURL).catch(() =>
                    setError(getAuthErrorMessage(null, 'support'))
                  )
                }>
                <View className="flex-row items-center gap-2">
                  <Icon as={CircleHelpIcon} size={14} className="text-muted-foreground" />
                  <Text variant="small" className="text-muted-foreground">
                    Need Help
                  </Text>
                </View>
              </Pressable>
            </View>

            <View className="pt-5">
              <View className="h-[49px] w-[220px]">
                <RadiousWordmark />
              </View>
              <Text variant="default" className="mt-3 text-[16px] text-muted-foreground">
                Find the spot that feels right tonight.
              </Text>
            </View>

            <View className="mt-8">
              <View className="gap-2">
                {appleAvailable ? (
                  <SocialAuthButton
                    disabled={isBusy || isSocialSignInBusy}
                    tone="light"
                    label="Continue with Apple"
                    icon={<AppleIcon />}
                    onPress={() => handleAppleSignIn().catch(() => undefined)}
                  />
                ) : null}

                <SocialAuthButton
                  disabled={isBusy || isSocialSignInBusy}
                  tone="dark"
                  label="Continue with Google"
                  icon={<GoogleIcon />}
                  onPress={() => handleGoogleSignIn().catch(() => undefined)}
                />
              </View>

              <View className="mb-3 mt-4 flex-row items-center gap-3">
                <View
                  className="h-px flex-1"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                />
                <Text className="text-[12px] text-muted-foreground">or</Text>
                <View
                  className="h-px flex-1"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                />
              </View>

              {/* Login/Signup tab switcher */}
              <Card
                tone="inset"
                className="relative rounded-[16px] p-1"
                onLayout={(event) => setTabWidth(event.nativeEvent.layout.width)}>
                {tabWidth > 0 ? (
                  <Animated.View
                    style={{
                      position: 'absolute',
                      top: 4,
                      bottom: 4,
                      width: tabWidth / 2 - 4,
                      borderRadius: 12,
                      backgroundColor: DESIGN_TOKENS.colors.accent,
                      transform: [{ translateX: indicatorTranslateX as any }],
                    }}
                  />
                ) : null}

                <View className="flex-row">
                  <AuthTab
                    active={mode === 'login'}
                    label="Sign In"
                    onPress={() => switchMode('login')}
                  />
                  <AuthTab
                    active={mode === 'signup'}
                    label="Create Account"
                    onPress={() => switchMode('signup')}
                  />
                </View>
              </Card>
            </View>

            <>
              {mode === 'signup' && signupStep === 'profile' ? (
                <Text variant="small" className="mt-5 leading-6 text-muted-foreground">
                  {helperCopy}
                </Text>
              ) : null}

              {mode === 'signup' && signupStep === 'profile' ? (
                <Pressable
                  accessibilityLabel="Back to account details"
                  accessibilityRole="button"
                  className="mt-3 min-h-[44px] self-start justify-center rounded-full px-3 -ml-3"
                  onPress={() => setSignupStep('credentials')}>
                  <Text variant="small" className="text-muted-foreground">
                    Back to account details
                  </Text>
                </Pressable>
              ) : null}

              <View className="mt-4">
                {mode === 'login' || signupStep === 'credentials' ? (
                  <>
                    <AuthField
                      label="Email address"
                      icon={MailIcon}
                      placeholder="Enter your email"
                      value={email}
                      onChangeText={(value) => {
                        emailInputTracker.changeText(value);
                        setEmail(value);
                      }}
                      onFieldFocus={() => emailInputTracker.focus()}
                      onFieldBlur={() => emailInputTracker.blur(email)}
                      keyboardType="email-address"
                      autoComplete="email"
                      textContentType="emailAddress"
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <AuthField
                      label="Password"
                      icon={KeyRoundIcon}
                      placeholder="Enter your password"
                      value={password}
                      onChangeText={(value) => {
                        passwordInputTracker.changeText(value);
                        setPassword(value);
                      }}
                      onFieldFocus={() => passwordInputTracker.focus()}
                      onFieldBlur={() => passwordInputTracker.blur(password)}
                      secureTextEntry
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      textContentType={mode === 'login' ? 'password' : 'newPassword'}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    {mode === 'signup' ? (
                      <AuthField
                        label="Confirm password"
                        icon={KeyRoundIcon}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChangeText={(value) => {
                          confirmPasswordInputTracker.changeText(value);
                          setConfirmPassword(value);
                        }}
                        onFieldFocus={() => confirmPasswordInputTracker.focus()}
                        onFieldBlur={() => confirmPasswordInputTracker.blur(confirmPassword)}
                        secureTextEntry
                        autoComplete="new-password"
                        textContentType="newPassword"
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <AuthField
                      label="Username"
                      icon={User2Icon}
                      placeholder="@yourhandle"
                      value={username}
                      onChangeText={(value) => {
                        usernameInputTracker.changeText(value);
                        setUsername(normalizeUsernameInput(value));
                      }}
                      onFieldFocus={() => usernameInputTracker.focus()}
                      onFieldBlur={() => usernameInputTracker.blur(username)}
                      autoCapitalize="none"
                      autoComplete="username-new"
                      textContentType="username"
                      autoCorrect={false}
                      maxLength={ACCOUNT_VALIDATION_LIMITS.usernameMaxLength}
                    />
                    <Text
                      variant="small"
                      className={cn(
                        '-mt-2 mb-3',
                        usernameValidationError ? 'text-destructive' : 'text-muted-foreground'
                      )}>
                      {usernameValidationError ?? USERNAME_HELP_COPY}
                    </Text>
                    <AuthField
                      label="Display name"
                      icon={User2Icon}
                      placeholder="How people should know you"
                      value={displayName}
                      onChangeText={(value) => {
                        displayNameInputTracker.changeText(value);
                        setDisplayName(value);
                      }}
                      onFieldFocus={() => displayNameInputTracker.focus()}
                      onFieldBlur={() => displayNameInputTracker.blur(displayName)}
                      autoCapitalize="words"
                      autoComplete="name"
                      textContentType="name"
                      maxLength={ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength}
                    />
                  </>
                )}
              </View>

              {error ? (
                <View
                  className="mt-5 rounded-[12px] border px-4 py-3"
                  accessibilityLiveRegion="polite"
                  accessibilityRole="alert"
                  style={{
                    borderColor: DESIGN_TOKENS.colors.accentEdge,
                    backgroundColor: DESIGN_TOKENS.colors.accentSoft,
                  }}>
                  <Text variant="small" className="text-primary">
                    {error}
                  </Text>
                </View>
              ) : null}

              {notice ? (
                <View
                  className="mt-5 rounded-[12px] border px-4 py-3"
                  accessibilityLiveRegion="polite"
                  style={{
                    borderColor: DESIGN_TOKENS.colors.success,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                  }}>
                  <Text variant="small" style={{ color: DESIGN_TOKENS.colors.success }}>
                    {notice}
                  </Text>
                </View>
              ) : null}

              <Button
                className={cn(
                  'mt-5 h-14 rounded-[14px] border',
                  isPrimaryDisabled ? 'border-border bg-card' : 'border-primary'
                )}
                style={{
                  backgroundColor: isPrimaryDisabled ? undefined : DESIGN_TOKENS.colors.accent,
                  opacity: 1,
                }}
                disabled={isPrimaryDisabled}
                onPress={() => handlePrimaryAction().catch(() => undefined)}>
                {isBusy ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text
                    className={cn(
                      'text-base font-bold',
                      isPrimaryDisabled ? 'text-muted-foreground' : 'text-white'
                    )}>
                    {primaryLabel}
                  </Text>
                )}
              </Button>

              {mode === 'login' ? (
                <Pressable
                  accessibilityLabel="Send password reset link"
                  accessibilityRole="button"
                  className="mt-4 min-h-[44px] self-center justify-center rounded-full px-4"
                  onPress={() => handlePasswordReset().catch(() => undefined)}>
                  <Text variant="small" className="text-primary">
                    Forgot password?
                  </Text>
                </Pressable>
              ) : null}

              {mode === 'signup' ? (
                <View className="mt-5 px-2">
                  <Text variant="small" className="text-center leading-6 text-muted-foreground">
                    By creating an account you agree to our{' '}
                    <Link href="/terms" asChild>
                      <Text variant="small" className="text-primary">
                        Terms of Service
                      </Text>
                    </Link>{' '}
                    and{' '}
                    <Link href="/privacy-policy" asChild>
                      <Text variant="small" className="text-primary">
                        Privacy Policy
                      </Text>
                    </Link>
                    .
                  </Text>
                </View>
              ) : null}

              <ContinueAsGuestButton />
            </>
        </View>
      </ScrollView>
    </View>
  );
}

function ContinueAsGuestButton() {
  const complete = useOnboarding((state) => state.complete);

  async function handleContinueAsGuest() {
    addSentryActionBreadcrumb({
      screen: 'sign_in',
      action: 'continue_as_guest',
      data: {},
    });
    await complete();
    router.replace('/(tabs)/events');
  }

  return (
    <Pressable
      accessibilityLabel="Continue as guest"
      accessibilityRole="button"
      className="mt-8 min-h-[44px] self-center justify-center rounded-full px-4"
      onPress={() => handleContinueAsGuest().catch(() => undefined)}>
      <Text
        variant="small"
        className="text-muted-foreground"
        style={{ textDecorationLine: 'underline' }}>
        Continue as guest
      </Text>
    </Pressable>
  );
}

function AuthTab({
  active,
  label,
  onPress,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      className="flex-1 px-3 py-3.5"
      onPress={onPress}>
      <Text
        variant="small"
        className={
          active
            ? 'text-center font-semibold text-primary-foreground'
            : 'text-center font-semibold text-foreground'
        }
        style={{ opacity: active ? 1 : 0.65 }}>
        {label}
      </Text>
    </Pressable>
  );
}

function SocialAuthButton({
  disabled,
  tone,
  label,
  icon,
  onPress,
}: {
  disabled: boolean;
  tone: 'light' | 'dark';
  label: string;
  icon: React.ReactNode;
  onPress: () => void;
}) {
  const isLight = tone === 'light';

  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={cn(
        'h-[52px] flex-row items-center justify-center gap-3 rounded-[12px] border px-4',
        isLight ? 'border-black/10 bg-white' : 'border-border bg-card',
        disabled && 'opacity-50'
      )}
      disabled={disabled}
      onPress={onPress}>
      <View className="-mt-0.5 size-5 items-center justify-center">{icon}</View>
      <Text
        className={cn(
          'text-[16px] font-medium leading-5 tracking-[-0.2px]',
          isLight ? 'text-black' : 'text-foreground'
        )}>
        {label}
      </Text>
    </Pressable>
  );
}

function GoogleIcon() {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
      <Path
        d="M23.49 12.27c0-.792-.071-1.55-.204-2.27H12v4.55h6.45a5.52 5.52 0 0 1-2.4 3.62v3.02h3.88c2.27-2.09 3.55-5.18 3.55-8.91Z"
        fill="#4285F4"
      />
      <Path
        d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3.02c-1.07.72-2.45 1.15-4.05 1.15-3.12 0-5.76-2.11-6.71-4.95H1.28v3.11A12 12 0 0 0 12 24Z"
        fill="#34A853"
      />
      <Path
        d="M5.29 14.27A7.21 7.21 0 0 1 4.92 12c0-.787.14-1.55.375-2.27V6.62H1.28A12 12 0 0 0 0 12c0 1.94.463 3.77 1.28 5.38l4.01-3.11Z"
        fill="#FBBC04"
      />
      <Path
        d="M12 4.78c1.76 0 3.34.605 4.59 1.79l3.44-3.44C17.95 1.21 15.23 0 12 0A12 12 0 0 0 1.28 6.62l4.01 3.11C6.24 6.89 8.88 4.78 12 4.78Z"
        fill="#EA4335"
      />
    </Svg>
  );
}

function AppleIcon() {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12.15 6.9c-.948 0-2.42-1.08-3.96-1.04-2.4.03-3.91 1.18-4.96 3.01-2.12 3.67-.546 9.1 1.52 12.09 1.01 1.45 2.21 3.09 3.79 3.04 1.52-.065 2.09-.987 3.94-.987 1.83 0 2.35.99 3.96.95 1.64-.026 2.68-1.48 3.68-2.95 1.16-1.69 1.64-3.33 1.66-3.42-.039-.013-3.18-1.22-3.22-4.86-.026-3.04 2.48-4.49 2.6-4.56-1.43-2.09-3.62-2.32-4.39-2.38-2-.156-3.67 1.09-4.61 1.09zM15.53 3.83c.843-1.01 1.4-2.43 1.25-3.83-1.21.052-2.66.805-3.53 1.82-.78.9-1.45 2.34-1.27 3.71 1.34.104 2.71-.688 3.56-1.7"
        fill="#000"
      />
    </Svg>
  );
}

function RadiousWordmark() {
  return (
    <Svg width="100%" height="100%" viewBox="0 0 310 69" fill="none">
      <Path
        d="M0 66.78V22.5H17.64V66.78H0ZM17.64 42.84L9.45 35.55C11.73 30.75 14.37 27.24 17.37 25.02C20.43 22.74 24.24 21.6 28.8 21.6C30.84 21.6 32.58 21.84 34.02 22.32C35.52 22.8 36.72 23.49 37.62 24.39L27.72 38.43C27.3 37.95 26.67 37.56 25.83 37.26C25.05 36.9 24.12 36.72 23.04 36.72C21.24 36.72 19.89 37.26 18.99 38.34C18.09 39.36 17.64 40.86 17.64 42.84ZM52.8 67.68C48.66 67.68 45 66.69 41.82 64.71C38.64 62.73 36.15 60.03 34.35 56.61C32.55 53.13 31.65 49.14 31.65 44.64C31.65 40.14 32.55 36.15 34.35 32.67C36.15 29.19 38.64 26.49 41.82 24.57C45 22.59 48.66 21.6 52.8 21.6C55.38 21.6 57.75 22.05 59.91 22.95C62.13 23.79 63.96 24.99 65.4 26.55C66.9 28.11 67.86 29.91 68.28 31.95V57.33C67.86 59.37 66.9 61.17 65.4 62.73C63.96 64.29 62.13 65.52 59.91 66.42C57.75 67.26 55.38 67.68 52.8 67.68ZM56.76 52.2C58.98 52.2 60.75 51.51 62.07 50.13C63.45 48.69 64.14 46.86 64.14 44.64C64.14 43.14 63.84 41.85 63.24 40.77C62.64 39.63 61.77 38.73 60.63 38.07C59.55 37.41 58.29 37.08 56.85 37.08C55.41 37.08 54.12 37.41 52.98 38.07C51.9 38.73 51.03 39.63 50.37 40.77C49.71 41.91 49.38 43.2 49.38 44.64C49.38 46.08 49.68 47.37 50.28 48.51C50.94 49.65 51.84 50.55 52.98 51.21C54.12 51.87 55.38 52.2 56.76 52.2ZM63.6 66.78V54.9L66.03 44.1L63.6 33.39V22.5H80.79V66.78H63.6ZM103.95 67.68C99.57 67.68 95.73 66.69 92.43 64.71C89.19 62.73 86.64 60.03 84.78 56.61C82.98 53.13 82.08 49.14 82.08 44.64C82.08 40.14 82.98 36.15 84.78 32.67C86.64 29.19 89.19 26.49 92.43 24.57C95.73 22.59 99.57 21.6 103.95 21.6C106.59 21.6 109.08 22.05 111.42 22.95C113.76 23.79 115.74 24.99 117.36 26.55C119.04 28.11 120.09 29.91 120.51 31.95V56.43C120.09 58.47 119.04 60.36 117.36 62.1C115.74 63.78 113.76 65.13 111.42 66.15C109.08 67.17 106.59 67.68 103.95 67.68ZM107.19 52.2C108.69 52.2 109.98 51.9 111.06 51.3C112.14 50.64 112.98 49.74 113.58 48.6C114.24 47.46 114.57 46.14 114.57 44.64C114.57 43.14 114.27 41.85 113.67 40.77C113.07 39.63 112.2 38.73 111.06 38.07C109.98 37.41 108.72 37.08 107.28 37.08C105.84 37.08 104.55 37.41 103.41 38.07C102.33 38.73 101.46 39.63 100.8 40.77C100.14 41.91 99.81 43.2 99.81 44.64C99.81 46.08 100.11 47.37 100.71 48.51C101.37 49.65 102.27 50.55 103.41 51.21C104.55 51.87 105.81 52.2 107.19 52.2ZM131.22 66.78H114.03V54.9L116.46 44.1L113.58 33.39V0.9H131.22V66.78ZM135.12 66.78V22.5H152.76V66.78H135.12ZM143.94 18.36C141.36 18.36 139.2 17.49 137.46 15.75C135.78 13.95 134.94 11.76 134.94 9.18C134.94 6.6 135.78 4.44 137.46 2.7C139.2 0.9 141.36 0 143.94 0C146.58 0 148.74 0.9 150.42 2.7C152.1 4.44 152.94 6.6 152.94 9.18C152.94 11.76 152.1 13.95 150.42 15.75C148.74 17.49 146.58 18.36 143.94 18.36ZM178.96 67.86C174.1 67.86 169.75 66.87 165.91 64.89C162.13 62.85 159.13 60.06 156.91 56.52C154.75 52.98 153.67 48.99 153.67 44.55C153.67 40.11 154.75 36.15 156.91 32.67C159.07 29.19 162.04 26.46 165.82 24.48C169.66 22.44 174.01 21.42 178.87 21.42C183.73 21.42 188.05 22.44 191.83 24.48C195.61 26.46 198.58 29.19 200.74 32.67C202.96 36.15 204.07 40.11 204.07 44.55C204.07 48.99 202.99 52.98 200.83 56.52C198.67 60.06 195.7 62.85 191.92 64.89C188.14 66.87 183.82 67.86 178.96 67.86ZM178.87 52.38C180.37 52.38 181.66 52.08 182.74 51.48C183.88 50.82 184.75 49.92 185.35 48.78C185.95 47.58 186.25 46.2 186.25 44.64C186.25 43.08 185.92 41.73 185.26 40.59C184.66 39.39 183.82 38.49 182.74 37.89C181.66 37.23 180.37 36.9 178.87 36.9C177.43 36.9 176.14 37.23 175 37.89C173.92 38.55 173.05 39.48 172.39 40.68C171.79 41.82 171.49 43.17 171.49 44.73C171.49 46.23 171.79 47.58 172.39 48.78C173.05 49.92 173.92 50.82 175 51.48C176.14 52.08 177.43 52.38 178.87 52.38ZM226.11 67.86C221.79 67.86 217.95 67.02 214.59 65.34C211.29 63.66 208.68 61.32 206.76 58.32C204.84 55.32 203.88 51.9 203.88 48.06V22.5H221.52V48.24C221.52 49.14 221.7 49.95 222.06 50.67C222.48 51.39 223.02 51.96 223.68 52.38C224.4 52.74 225.21 52.92 226.11 52.92C227.37 52.92 228.42 52.5 229.26 51.66C230.16 50.76 230.61 49.62 230.61 48.24V22.5H248.25V48.06C248.25 51.96 247.29 55.41 245.37 58.41C243.51 61.35 240.9 63.66 237.54 65.34C234.24 67.02 230.43 67.86 226.11 67.86ZM268.64 68.13C265.88 68.13 263.12 67.77 260.36 67.05C257.6 66.33 255.02 65.34 252.62 64.08C250.28 62.82 248.3 61.38 246.68 59.76L255.95 50.22C257.39 51.66 259.1 52.8 261.08 53.64C263.12 54.48 265.25 54.9 267.47 54.9C268.49 54.9 269.27 54.78 269.81 54.54C270.41 54.24 270.71 53.85 270.71 53.37C270.71 52.53 270.2 51.9 269.18 51.48C268.22 51.06 266.96 50.67 265.4 50.31C263.9 49.95 262.28 49.5 260.54 48.96C258.86 48.36 257.24 47.58 255.68 46.62C254.12 45.6 252.86 44.22 251.9 42.48C250.94 40.68 250.46 38.4 250.46 35.64C250.46 32.82 251.24 30.33 252.8 28.17C254.42 25.95 256.67 24.21 259.55 22.95C262.49 21.63 265.88 20.97 269.72 20.97C273.62 20.97 277.4 21.63 281.06 22.95C284.72 24.27 287.63 26.25 289.79 28.89L280.43 38.43C278.93 36.75 277.28 35.64 275.48 35.1C273.68 34.5 272.18 34.2 270.98 34.2C269.84 34.2 269.03 34.38 268.55 34.74C268.13 35.04 267.92 35.43 267.92 35.91C267.92 36.57 268.4 37.11 269.36 37.53C270.32 37.95 271.55 38.34 273.05 38.7C274.61 39.06 276.23 39.54 277.91 40.14C279.59 40.74 281.18 41.58 282.68 42.66C284.24 43.74 285.5 45.18 286.46 46.98C287.42 48.72 287.9 51 287.9 53.82C287.9 58.2 286.16 61.68 282.68 64.26C279.2 66.84 274.52 68.13 268.64 68.13Z"
        fill={DESIGN_TOKENS.colors.text}
      />
      <Path
        d="M299.9 67.86C297.08 67.86 294.74 66.9 292.88 64.98C291.02 63.06 290.09 60.72 290.09 57.96C290.09 55.14 291.02 52.77 292.88 50.85C294.74 48.93 297.08 47.97 299.9 47.97C302.72 47.97 305.03 48.93 306.83 50.85C308.69 52.77 309.62 55.14 309.62 57.96C309.62 60.72 308.69 63.06 306.83 64.98C305.03 66.9 302.72 67.86 299.9 67.86Z"
        fill={DESIGN_TOKENS.colors.accent}
      />
    </Svg>
  );
}

function AuthField({
  label,
  icon,
  placeholder,
  value,
  onChangeText,
  onFocus,
  onFieldFocus,
  onFieldBlur,
  secureTextEntry,
  keyboardType,
  autoCapitalize,
  autoCorrect,
  maxLength,
  autoComplete,
  textContentType,
}: {
  label: string;
  icon: React.ComponentProps<typeof Icon>['as'];
  placeholder: string;
  value: string;
  onChangeText: (value: string) => void;
  onFocus?: () => void;
  onFieldFocus?: () => void;
  onFieldBlur?: () => void;
  secureTextEntry?: boolean;
  keyboardType?: React.ComponentProps<typeof TextInput>['keyboardType'];
  autoCapitalize?: React.ComponentProps<typeof TextInput>['autoCapitalize'];
  autoCorrect?: boolean;
  maxLength?: number;
  autoComplete?: React.ComponentProps<typeof TextInput>['autoComplete'];
  textContentType?: React.ComponentProps<typeof TextInput>['textContentType'];
}) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  return (
    <View
      className={cn(
        'relative mb-3 flex-row items-center rounded-[12px] border bg-card px-4',
        isFocused ? 'border-primary' : 'border-border'
      )}
      style={{
        backgroundColor: isFocused ? DESIGN_TOKENS.colors.accentSoft : DESIGN_TOKENS.colors.surface,
        boxShadow: isFocused ? `0 0 0 2px ${DESIGN_TOKENS.colors.accentEdge}` : 'none',
      } as any}>
      <Icon
        as={icon}
        size={18}
        className={cn('mr-3 text-muted-foreground', isFocused && 'text-primary')}
      />
      <TextInput
        accessibilityLabel={label}
        accessibilityHint={secureTextEntry ? 'Double tap to edit a secure text field.' : undefined}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
        secureTextEntry={secureTextEntry && !isPasswordVisible}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        maxLength={maxLength}
        autoComplete={autoComplete}
        textContentType={textContentType}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
          onFieldFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          onFieldBlur?.();
        }}
        className={cn('flex-1 p-0 text-foreground', secureTextEntry && 'pr-12')}
        style={{
          fontFamily: DESIGN_TOKENS.typography.fonts.body,
          fontSize: 16,
          lineHeight: 20,
          color: DESIGN_TOKENS.colors.text,
          padding: 0,
          paddingVertical: 16,
          margin: 0,
          textAlignVertical: 'center',
          outlineStyle: 'none',
          boxShadow: 'none',
        } as any}
      />
      {secureTextEntry ? (
        <Pressable
          accessibilityLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
          accessibilityRole="button"
          accessibilityState={{ selected: isPasswordVisible }}
          className="absolute bottom-0 right-2 top-0 w-11 items-center justify-center"
          onPress={() => setIsPasswordVisible((v) => !v)}>
          <Icon
            as={isPasswordVisible ? EyeOffIcon : EyeIcon}
            size={18}
            className={cn('text-muted-foreground', isFocused && 'text-primary')}
          />
        </Pressable>
      ) : null}
    </View>
  );
}
